import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Settings, ShieldCheck, AlertTriangle, Send, GitMerge, Menu, X } from 'lucide-react';
import { useGmailApi, useGmailHealth } from '../hooks/useGmailApi';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { LabelList } from '../components/Gmail/LabelList';
import { MessageList } from '../components/Gmail/MessageList';
import { MessageViewer } from '../components/Gmail/MessageViewer';
import { ComposeModal } from '../components/Gmail/ComposeModal';
import { FullscreenViewerModal } from '../components/Gmail/FullscreenViewerModal';
import { GmailMessage } from '../types/api';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100dvh;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  gap: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};

  h1 {
    margin: 0;
    font-size: 20px;
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  p {
    margin: 0;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    h1 { font-size: 18px; }
    p { display: none; }
  }
`;

const StatusGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const StatusChip = styled.span<{ $variant: 'success' | 'warning' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  background: ${({ $variant }) =>
    $variant === 'success'
      ? 'rgba(16, 185, 129, 0.12)'
      : $variant === 'warning'
        ? 'rgba(245, 158, 11, 0.12)'
        : 'rgba(239, 68, 68, 0.12)'};
  color: ${({ $variant }) =>
    $variant === 'success'
      ? '#0f766e'
      : $variant === 'warning'
        ? '#b45309'
        : '#b91c1c'};
  border: 1px solid ${({ $variant }) =>
    $variant === 'success'
      ? 'rgba(16, 185, 129, 0.4)'
      : $variant === 'warning'
        ? 'rgba(245, 158, 11, 0.4)'
        : 'rgba(239, 68, 68, 0.4)'};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: nowrap;
  justify-content: flex-end;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    gap: ${({ theme }) => theme.spacing[2]};
  }
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'outline'; $iconOnly?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme, $iconOnly }) => $iconOnly ? theme.spacing[2] : `${theme.spacing[2]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  border: 1px solid ${({ theme, $variant }) =>
    $variant === 'primary' ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $variant }) =>
    $variant === 'primary' ? theme.colors.primary : 'transparent'};
  color: ${({ theme, $variant }) =>
    $variant === 'primary' ? 'white' : theme.colors.textSecondary};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  span {
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      display: ${({ $iconOnly }) => $iconOnly ? 'none' : 'none'};
    }
  }
`;

const HealthIndicator = styled.div<{ $healthy: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background: ${({ $healthy }) => ($healthy ? '#10b98115' : '#ef444415')};
  border: 1px solid ${({ $healthy }) => ($healthy ? '#10b981' : '#ef4444')};

  .indicator-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $healthy }) => ($healthy ? '#10b981' : '#ef4444')};
    ${({ $healthy }) =>
    $healthy &&
    `
      animation: pulse 2s ease-in-out infinite;

      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }
    `}
  }

  .indicator-text {
    font-size: 12px;
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ $healthy }) => ($healthy ? '#10b981' : '#ef4444')};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    .indicator-text { display: none; }
    padding: ${({ theme }) => theme.spacing[2]};
  }
`;

const Content = styled.main`
  display: flex;
  min-height: 0;
  overflow: hidden;
  position: relative;
`;

const LabelsSidebar = styled.aside<{ $collapsed: boolean; $mobileOpen: boolean }>`
  width: ${({ $collapsed }) => ($collapsed ? '70px' : '240px')};
  flex-shrink: 0;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease, transform 0.3s ease;
  z-index: 20;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 260px;
    transform: ${({ $mobileOpen }) => ($mobileOpen ? 'translateX(0)' : 'translateX(-100%)')};
    box-shadow: ${({ theme, $mobileOpen }) => ($mobileOpen ? theme.shadows.xl : 'none')};
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: none;
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
    transition: opacity 0.3s ease;
    z-index: 15;
  }
`;

const MainArea = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
  min-height: 0;
  min-width: 0;
`;

const MessagingLayout = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
`;

const MessagesPanel = styled.div<{ $messageSelected: boolean }>`
  width: 420px;
  flex-shrink: 0;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 350px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    display: ${({ $messageSelected }) => ($messageSelected ? 'none' : 'flex')};
    border-right: none;
  }
`;

const MessageViewerContainer = styled.div<{ $active: boolean }>`
  flex: 1;
  min-width: 0;
  display: flex;
  background: ${({ theme }) => theme.colors.background};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: ${({ $active }) => ($active ? 'flex' : 'none')};
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
  }
`;

const PlaceholderState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[3]};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: ${({ theme }) => theme.spacing[6]};

  svg {
    width: 48px;
    height: 48px;
    opacity: 0.4;
  }
`;

const HighlightCard = styled.div`
  margin: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]} ${theme.spacing[3]}`};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
  }
`;

const GmailIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const userId = user?.uid;

  const [isComposeModalOpen, setComposeModalOpen] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [fullscreenMessage, setFullscreenMessage] = useState<GmailMessage | null>(null);
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [composeDefaults, setComposeDefaults] = useState<{
    to?: string;
    subject?: string;
    body?: string;
    cc?: string[];
    bcc?: string[];
  }>({});

  const {
    labels,
    messages,
    selectedLabel,
    hasToken,
    tokenEmail,
    tokenActive,
    tokenExpired,
    isLoadingLabels,
    isLoadingMessages,
    isSendingMessage,
    isCreatingDraft,
    isModifyingLabels,
    selectLabel,
    sendMessage,
    createDraft,
    modifyLabels,
    refetchLabels,
    refetchMessages,
    useMessage,
  } = useGmailApi(userId);

  const { data: healthData, isLoading: isLoadingHealth } = useGmailHealth();
  const messageQuery = useMessage(selectedMessageId);

  useEffect(() => {
    if (!hasToken) {
      setSelectedMessageId(null);
    }
  }, [hasToken]);

  useEffect(() => {
    if (messages.length === 0) {
      setSelectedMessageId(null);
      return;
    }

    if (!selectedMessageId) {
      // Don't auto-select on mobile to show list first
      if (window.innerWidth > 768) {
        setSelectedMessageId(messages[0].id);
      }
      return;
    }

    const stillExists = messages.some(message => message.id === selectedMessageId);
    if (!stillExists) {
      setSelectedMessageId(messages[0]?.id ?? null);
    }
  }, [messages, selectedMessageId]);

  const handleLabelSelect = useCallback(
    (labelId: string | null) => {
      selectLabel(labelId);
      setSelectedMessageId(null);
      setMobileSidebarOpen(false); // Close mobile sidebar on selection
    },
    [selectLabel]
  );

  const handleMessageSelect = useCallback((messageId: string) => {
    setSelectedMessageId(messageId);
  }, []);

  const handleEnterFullscreen = (message: GmailMessage) => {
    setFullscreenMessage(message);
  };

  const handleExitFullscreen = () => {
    setFullscreenMessage(null);
  };

  const handleBackToMessages = () => {
    setSelectedMessageId(null);
  };

  const handleToggleRead = useCallback(
    async (message: GmailMessage, unread: boolean) => {
      if (unread) {
        await modifyLabels(message.id, undefined, ['UNREAD']);
      } else {
        await modifyLabels(message.id, ['UNREAD']);
      }
    },
    [modifyLabels]
  );

  // Auto-collapse sidebar on smaller screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(true);
      } else {
        setSidebarCollapsed(false);
      }
    };

    // Initial check
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  const closeComposeModal = useCallback(() => {
    setComposeModalOpen(false);
    setComposeDefaults({});
  }, []);

  const openComposeModal = useCallback((defaults: typeof composeDefaults = {}) => {
    setComposeDefaults(defaults);
    setComposeModalOpen(true);
  }, []);

  const handleSendMessage = useCallback(
    async (payload: Parameters<typeof sendMessage>[0]) => {
      await sendMessage(payload);
      closeComposeModal();
      await refetchMessages();
    },
    [sendMessage, refetchMessages, closeComposeModal]
  );

  const handleSaveDraft = useCallback(
    async (payload: Parameters<typeof createDraft>[0]) => {
      await createDraft(payload);
      closeComposeModal();
      await refetchMessages();
    },
    [createDraft, refetchMessages, closeComposeModal]
  );

  const accountStatus = useMemo<'success' | 'warning' | 'danger'>(() => {
    if (!hasToken) return 'danger';
    if (tokenExpired) return 'warning';
    if (tokenActive) return 'success';
    return 'warning';
  }, [hasToken, tokenActive, tokenExpired]);

  const accountStatusText = useMemo(() => {
    if (!hasToken) return 'Conta desconectada';
    if (tokenExpired) return 'Token expirado';
    if (tokenActive) return 'Conta ativa';
    return 'Aguardando validação';
  }, [hasToken, tokenActive, tokenExpired]);

  const healthStatus = useMemo(() => {
    if (isLoadingHealth) return 'Verificando...';
    if (healthData?.status === 'success') return 'Online';
    return 'Indisponível';
  }, [healthData?.status, isLoadingHealth]);

  const isHealthy = healthData?.status === 'success';

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <MobileMenuButton onClick={() => setMobileSidebarOpen(true)}>
              <Menu size={20} />
            </MobileMenuButton>
            <HeaderTitle>
              <h1>Integração Gmail</h1>
              <p>Gerencie etiquetas, visualize mensagens e envie e-mails sem sair da Loopra.</p>
            </HeaderTitle>
          </div>
          <StatusGroup>
            <StatusChip $variant={accountStatus}>{accountStatusText}</StatusChip>
            {tokenEmail && (
              <StatusChip $variant="success">
                <ShieldCheck size={14} />
                {tokenEmail}
              </StatusChip>
            )}
          </StatusGroup>
        </HeaderLeft>

        <HeaderActions>
          <ActionButton
            type="button"
            onClick={() => navigate('/gmail/config')}
            $variant="outline"
            title="Configurar"
          >
            <Settings size={16} />
            <span>Configurar</span>
          </ActionButton>

          <ActionButton
            type="button"
            onClick={() => navigate('/gmail/automation')}
            $variant="outline"
            title="Automatizar Fluxos"
          >
            <GitMerge size={16} />
            <span>Automatizar</span>
          </ActionButton>

          <ActionButton
            type="button"
            $variant="primary"
            disabled={!hasToken}
            onClick={() => openComposeModal()}
            title="Novo e-mail"
          >
            <Send size={16} />
            <span>Novo e-mail</span>
          </ActionButton>

          <HealthIndicator $healthy={isHealthy}>
            <div className="indicator-dot" />
            <div className="indicator-text">{healthStatus}</div>
          </HealthIndicator>
        </HeaderActions>
      </Header>

      <Content>
        <Overlay $isOpen={isMobileSidebarOpen} onClick={() => setMobileSidebarOpen(false)} />
        <LabelsSidebar $collapsed={isSidebarCollapsed} $mobileOpen={isMobileSidebarOpen}>
          <LabelList
            labels={labels}
            selectedLabel={selectedLabel}
            onSelectLabel={handleLabelSelect}
            onRefresh={refetchLabels}
            isLoading={isLoadingLabels}
            activeCount={messages.length}
            collapsed={isSidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(!isSidebarCollapsed)}
          />
        </LabelsSidebar>

        <MainArea>
          {!hasToken ? (
            <PlaceholderState>
              <AlertTriangle size={42} />
              <h3>Conta desconectada</h3>
              <p>
                Conecte sua conta Gmail para visualizar mensagens e gerenciar suas etiquetas dentro da Loopra.
              </p>
              <ActionButton type="button" $variant="primary" onClick={() => navigate('/gmail/config')}>
                Conectar Conta
              </ActionButton>
            </PlaceholderState>
          ) : (
            <>
              <HighlightCard>
                <ShieldCheck size={18} />
                As ações realizadas aqui respeitam os escopos autorizados no Google Cloud. Revogue o acesso quando quiser.
              </HighlightCard>

              <MessagingLayout>
                <MessagesPanel $messageSelected={!!selectedMessageId}>
                  <MessageList
                    messages={messages}
                    selectedMessageId={selectedMessageId}
                    onSelectMessage={handleMessageSelect}
                    isLoading={isLoadingMessages}
                  />
                </MessagesPanel>

                <MessageViewerContainer $active={!!selectedMessageId}>
                  <MessageViewer
                    messageId={selectedMessageId}
                    message={messageQuery.data?.data}
                    isLoading={messageQuery.isLoading}
                    error={messageQuery.error}
                    isTogglingRead={isModifyingLabels}
                    onToggleRead={handleToggleRead}
                    onEnterFullscreen={handleEnterFullscreen}
                    onBack={handleBackToMessages} // Pass back handler for mobile
                    onReply={message => {
                      openComposeModal({
                        to: message.from || '',
                        subject: message.subject ? `Re: ${message.subject}` : 'Re:',
                        body: `\n\n---------- Mensagem Recebida ----------\n${message.body || message.snippet || ''}`,
                        cc: message.cc,
                      });
                    }}
                    onForward={message => {
                      openComposeModal({
                        subject: message.subject ? `Fwd: ${message.subject}` : 'Fwd:',
                        body: `\n\n---------- Início da mensagem encaminhada ----------\nDe: ${message.from}\nPara: ${message.to}\nAssunto: ${message.subject}\n\n${message.body || message.snippet || ''}`,
                      });
                    }}
                  />
                </MessageViewerContainer>
              </MessagingLayout>
            </>
          )}
        </MainArea>
      </Content>

      <ComposeModal
        isOpen={isComposeModalOpen}
        onClose={closeComposeModal}
        onSent={closeComposeModal}
        onDraftSaved={closeComposeModal}
        onSendMessage={handleSendMessage}
        onSaveDraft={handleSaveDraft}
        isSending={isSendingMessage}
        isSavingDraft={isCreatingDraft}
        initialTo={composeDefaults.to}
        initialSubject={composeDefaults.subject}
        initialBody={composeDefaults.body}
        initialCc={composeDefaults.cc}
        initialBcc={composeDefaults.bcc}
      />

      {fullscreenMessage && (
        <FullscreenViewerModal message={fullscreenMessage} onClose={handleExitFullscreen} />
      )}
    </Container>
  );
};

export default GmailIntegration;
